import { Dispatch, SetStateAction } from "react";
import { TypeOptions, toast } from "react-toastify";

import { ToastButtonContentType } from "../Components/ErrorHandling/ErrorHandler";
import { Center } from "../Models/Center";
import { Customer } from "../Models/Customer";

// Keys for error handling behaviors. Each request should have a unique value.
enum Error {
  DeleteNote,
  DeleteToken,
  GetAreasOfInterest,
  GetBusinessUnits,
  GetCenterCalendar,
  GetCenters,
  GetCentersMessages,
  GetCustomer,
  GetCustomerAppointments,
  GetCustomerNotes,
  GetCustomerOpportunities,
  GetCustomers,
  GetDialTimeMinMax,
  GetDisqualifyingReason,
  GetDynamicContent,
  GetIvrCampaigns,
  GetIvrEvents,
  GetIvrSession,
  GetIvrUsers,
  GetLead,
  GetLeadResultTypes,
  GetNotOfferedProcedures,
  GetOrgBookingAge,
  GetOrgNewLeadRate,
  GetPhoneNumbers,
  GetUserBookingAge,
  GetUserMe,
  GetUserNewLeadRate,
  PostCenterMessage,
  PostCenterRelease,
  PostCenterReservation,
  PostCustomer,
  PostCustomerAppointment,
  PostCustomerNote,
  PostIvrToken,
  PostLeadDial,
  PostLeadManual,
  PostLeadResult,
  PostOpportunityNote,
  PostSignalRNegotiate,
  PostToken,
  PutCenterMessage,
  PutCenters,
  PutCustomer,
  PutCustomerNote,
  PutCustomerReset,
  PutIvrLeadState,
  PutIvrState,
  PutIvrUserCampaigns,
  PutLead,
  PutLeadReceived,
  PutLeadResult,
  PutPhoneNumbers,
  PutToken,
  PutUserMe,
  PutUserPoll,
  UncaughtNetwork
}

type SessionProps = {
  customer: Customer;
  centers: Center[];
  setRetry: Dispatch<SetStateAction<boolean>>;
  setIsDismissed: Dispatch<SetStateAction<boolean>>;
};

type AppProps = {
  setRetry: Dispatch<SetStateAction<boolean>>;
  setIsDismissed: Dispatch<SetStateAction<boolean>>;
};

// The behavior configuration to execute for a given error.
export interface ErrorBehavior {
  // The message to display if the request GemError has no message.
  backupMessage: string;
  // The toast style (color) to use
  toastStyle: TypeOptions;
  // The message instructing the user how to recover from the error.
  recoveryMessage: string;
  // Builds a script action to be executed when the toast is dismissed
  action?: (props?: SessionProps | AppProps) => () => void;
  // The title of the dismiss button
  actionBtnTitle?: ToastButtonContentType | string;
  dismiss?: (props?: SessionProps | AppProps) => () => void;
}

// action that opens a new tab to the given path
const reloadWindow = (): void => window.location.reload();

// action that opens a new tab to the given path
const openNewTab = (path: string): void => {
  window.open(path, undefined, "noopener,noreferrer");
};

const buildRetryAction = (props?: AppProps): void => {
  props?.setRetry(retry => !retry);
};

const buildDismissAction = (props?: AppProps): void => {
  props?.setIsDismissed(true);
};

// build a path to the props's zenoti guest
const buildZenotiGuestPath = (props?: SessionProps): string =>
  props ? `${process.env.REACT_APP_CRM_BASEURL}/Guests/GuestProfile.aspx?UserId=${props.customer.externalId}` : "";

// build a path to the zenoti appointments page
const buildZenotiAppointmentPath = (props?: SessionProps): string =>
  props
    ? `${process.env.REACT_APP_CRM_BASEURL}/appointmentbook?guestId=${props.customer.externalId}&centerId=${
        props.centers.find(c => c.id === props.customer.centerId)?.externalId
      }`
    : "";

// reload the window

const ErrorBehaviors: Record<Error, Record<number | "default", ErrorBehavior | null>> = {
  [Error.UncaughtNetwork]: {
    default: {
      backupMessage: "Failed to communicate with the server.",
      toastStyle: "error",
      recoveryMessage: "Please ensure you are connected to the internet and inform your supervisor.",
      action: (props?: SessionProps | AppProps) => () => buildRetryAction(props as AppProps),
      actionBtnTitle: "Retry",
      dismiss: (props?: SessionProps | AppProps) => () => buildDismissAction(props as AppProps)
    }
  },
  [Error.DeleteNote]: {
    default: {
      backupMessage: "Failed to delete the Note for the old Guest.",
      toastStyle: "warning",
      recoveryMessage: "You will need to delete this Note manually in Zenoti.",
      action: (props?: SessionProps | AppProps) => () => openNewTab(buildZenotiGuestPath(props as SessionProps))
    }
  },
  [Error.DeleteToken]: {
    default: {
      backupMessage: "Failed to end user props.",
      toastStyle: "warning",
      recoveryMessage: ""
    }
  },
  [Error.GetPhoneNumbers]: {
    default: {
      backupMessage: "Failed to get the list of blocked phone numbers.",
      toastStyle: "error",
      recoveryMessage: "Please inform your supervisor."
    }
  },
  [Error.GetAreasOfInterest]: {
    default: {
      backupMessage: "Failed to get Areas of Interest.",
      toastStyle: "error",
      recoveryMessage: "Please inform your supervisor."
    }
  },
  [Error.GetBusinessUnits]: {
    default: {
      backupMessage: "Failed to get Business Units.",
      toastStyle: "error",
      recoveryMessage: "Please inform your supervisor."
    }
  },
  [Error.GetCenters]: {
    default: {
      backupMessage: "Failed to get Centers.",
      toastStyle: "error",
      recoveryMessage: "Please inform your supervisor."
    }
  },
  [Error.GetCenterCalendar]: {
    400: {
      backupMessage: "Failed to get the schedule for the Center.",
      toastStyle: "error",
      recoveryMessage: "Please inform your supervisor, and then book directly in Zenoti.",
      action: (props?: SessionProps | AppProps) => () => openNewTab(buildZenotiAppointmentPath(props as SessionProps)),
      actionBtnTitle: "Zenoti"
    },
    404: null,
    default: {
      backupMessage: "Failed to get the schedule for the Center.",
      toastStyle: "error",
      recoveryMessage: "You will need to book directly in Zenoti.",
      action: (props?: SessionProps | AppProps) => () => openNewTab(buildZenotiAppointmentPath(props as SessionProps)),
      actionBtnTitle: "Zenoti"
    }
  },
  [Error.GetCentersMessages]: {
    403: {
      backupMessage: "Failed to get the Messages for all Centers.",
      toastStyle: "error",
      recoveryMessage: "You are not authorized to access this information. Please inform your supervisor."
    },
    default: {
      backupMessage: "Failed to get Center Messages.",
      toastStyle: "error",
      recoveryMessage: "Please inform your supervisor."
    }
  },
  [Error.GetCustomer]: {
    default: {
      backupMessage: "Unable to get Guest information.",
      toastStyle: "error",
      recoveryMessage: "Please note any information about the Guest (phone number, etc) and inform your supervisor."
    }
  },
  [Error.GetCustomerOpportunities]: {
    default: {
      backupMessage: "Unable to get Guest information.",
      toastStyle: "error",
      recoveryMessage: "Please note any information about the Guest (phone number, etc) and inform your supervisor."
    }
  },
  [Error.GetCustomers]: {
    default: {
      backupMessage: "Unable to get search results.",
      toastStyle: "error",
      recoveryMessage: "Please inform your supervisor, and then proceed with a new Guest."
    }
  },
  [Error.GetCustomerAppointments]: {
    default: {
      backupMessage: "Unable to retrieve Guest Appointment history.",
      toastStyle: "warning",
      recoveryMessage: "You can view this information directly in Zenoti.",
      action: (props?: SessionProps | AppProps) => () => openNewTab(buildZenotiGuestPath(props as SessionProps)),
      actionBtnTitle: "Zenoti"
    }
  },
  [Error.GetCustomerNotes]: {
    default: {
      backupMessage: "Unable to retrieve Guest notes.",
      toastStyle: "warning",
      recoveryMessage: "You can view them directly in Zenoti.",
      action: (props?: SessionProps | AppProps) => () => openNewTab(buildZenotiGuestPath(props as SessionProps)),
      actionBtnTitle: "Zenoti"
    }
  },
  [Error.GetDialTimeMinMax]: {
    default: {
      backupMessage: "Failed to get Minimum and Maximum Dial Times..",
      toastStyle: "error",
      recoveryMessage: "Please inform your supervisor."
    }
  },
  [Error.GetDisqualifyingReason]: {
    default: {
      backupMessage: "Failed to get Disqualifying Reasons.",
      toastStyle: "error",
      recoveryMessage: "Please inform your supervisor."
    }
  },
  [Error.GetIvrCampaigns]: {
    default: {
      backupMessage: "Failed to get the list of Ivr Campaigns.",
      toastStyle: "error",
      recoveryMessage: "Please submit a ticket for Ivr Campaigns."
    }
  },
  [Error.GetIvrEvents]: {
    304: null,
    default: {
      backupMessage: "Connection to MAX has been lost.",
      toastStyle: "error",
      recoveryMessage: "Please ensure MAX is open. Attempting reconnection..."
    }
  },
  [Error.GetIvrSession]: {
    400: {
      backupMessage: "Unable to communicate with MAX.",
      toastStyle: "error",
      recoveryMessage: "Please open MAX then refresh the page.",
      actionBtnTitle: "InContact"
    },
    404: {
      backupMessage: "Unable to communicate with MAX.",
      toastStyle: "error",
      recoveryMessage: "Please open MAX then refresh the page.",
      actionBtnTitle: "InContact"
    },
    409: {
      backupMessage: "Unable to communicate with MAX.",
      toastStyle: "error",
      recoveryMessage: "Please open MAX then refresh the page.",
      action: (): (() => void) => reloadWindow,
      actionBtnTitle: "Refresh"
    },
    401: {
      backupMessage: "Unable to communicate with MAX.",
      toastStyle: "error",
      recoveryMessage: "Please open MAX then refresh the page.",
      action: (): (() => void) => reloadWindow,
      actionBtnTitle: "Refresh"
    },
    default: {
      backupMessage: "Failed to communicate with InContact.",
      toastStyle: "error",
      recoveryMessage: "Please contact your supervisor and log out and then in again."
    }
  },
  [Error.GetIvrUsers]: {
    default: {
      backupMessage: "Failed to get the list of Ivr users.",
      toastStyle: "error",
      recoveryMessage: "Please submit a ticket for Ivr users."
    }
  },
  [Error.GetLead]: {
    401: null,
    default: {
      backupMessage: "Failed to load a new contact.",
      toastStyle: "error",
      recoveryMessage: "Please inform your supervisor."
    }
  },
  [Error.PostLeadManual]: {
    default: {
      backupMessage: "Failed to create a Manual Lead.",
      toastStyle: "error",
      recoveryMessage: "Please inform your supervisor."
    }
  },
  [Error.GetLeadResultTypes]: {
    default: {
      backupMessage: "Failed to get Lead Result Types.",
      toastStyle: "error",
      recoveryMessage: "Please inform your supervisor."
    }
  },
  [Error.GetNotOfferedProcedures]: {
    default: {
      backupMessage: "Failed to get Not Offered Procedures.",
      toastStyle: "error",
      recoveryMessage: "Please inform your supervisor."
    }
  },
  [Error.GetOrgBookingAge]: {
    default: {
      backupMessage: "Failed to get the organization's booking age.",
      toastStyle: "warning",
      recoveryMessage: "You will be unable to view the report."
    }
  },
  [Error.GetOrgNewLeadRate]: {
    default: {
      backupMessage: "Failed to get the the organization's Lead rate.",
      toastStyle: "warning",
      recoveryMessage: "You will be unable to view the report."
    }
  },
  [Error.GetDynamicContent]: {
    default: {
      backupMessage: "Failed to get FAQs and Rebuttal.",
      toastStyle: "error",
      recoveryMessage: "Please refresh your browser.",
      action: (props?: SessionProps | AppProps) => () => buildRetryAction(props as AppProps),
      actionBtnTitle: "Retry",
      dismiss: (props?: SessionProps | AppProps) => () => buildDismissAction(props as AppProps)
    }
  },
  [Error.GetUserMe]: {
    default: {
      backupMessage: "Failed to get User Information.",
      toastStyle: "error",
      recoveryMessage: "Please inform your supervisor."
    }
  },
  [Error.GetUserBookingAge]: {
    default: {
      backupMessage: "Failed to get user's booking age.",
      toastStyle: "warning",
      recoveryMessage: "You will be unable to view the report."
    }
  },
  [Error.GetUserNewLeadRate]: {
    default: {
      backupMessage: "Failed to get the the user's Lead rate.",
      toastStyle: "warning",
      recoveryMessage: "You will be unable to view the report."
    }
  },
  [Error.PostCenterMessage]: {
    default: {
      backupMessage: "There was an error creating the center message.",
      toastStyle: "error",
      recoveryMessage: "Please inform your supervisor."
    }
  },
  [Error.PostCenterRelease]: {
    default: {
      backupMessage: "There was an error cancelling your Appointment reservation.",
      toastStyle: "warning",
      recoveryMessage: "Please inform your supervisor."
    }
  },
  [Error.PostCenterReservation]: {
    default: {
      backupMessage: "There was an error reserving your Appointment.",
      toastStyle: "warning",
      recoveryMessage: "Please try again."
    }
  },
  [Error.PostCustomer]: {
    400: {
      backupMessage: "Unable to update the Guest.",
      toastStyle: "warning",
      recoveryMessage: "Please check your entries for valid inputs.",
      actionBtnTitle: "info"
    },
    409: null,
    default: {
      backupMessage: "Unable to create the Guest.",
      toastStyle: "error",
      recoveryMessage: "Please inform your supervisor."
    }
  },
  [Error.PostCustomerAppointment]: {
    409: {
      backupMessage: "Unable to create the Appointment.",
      toastStyle: "warning",
      recoveryMessage: "Please try again."
    },
    default: {
      backupMessage: "Unable to create the Appointment.",
      toastStyle: "warning",
      recoveryMessage: "You will need to book directly in Zenoti.",
      action: (props?: SessionProps | AppProps) => () => openNewTab(buildZenotiAppointmentPath(props as SessionProps)),
      actionBtnTitle: "Zenoti"
    }
  },
  [Error.PostCustomerNote]: {
    default: {
      backupMessage: "Unable to create the Guest note.",
      toastStyle: "error",
      recoveryMessage: "Please copy the note and paste it into Zenoti.",
      action: (props?: SessionProps | AppProps) => () => openNewTab(buildZenotiGuestPath(props as SessionProps)),
      actionBtnTitle: "Zenoti"
    }
  },
  [Error.PostIvrToken]: {
    401: null,
    default: {
      backupMessage: "The server failed to validate your credentials.",
      toastStyle: "error",
      recoveryMessage: "Please check that you are using your inContact credentials."
    }
  },
  [Error.PostLeadDial]: {
    default: {
      backupMessage: "The server failed to initiate an outbound dial to InContact.",
      toastStyle: "warning",
      recoveryMessage: "Please call manually within InContact"
    }
  },
  [Error.PostLeadResult]: {
    400: {
      backupMessage: "GEM was updated but failed to do so in MAX.",
      toastStyle: "warning",
      recoveryMessage: "You will need to disposition manually in MAX.",
      actionBtnTitle: "InContact"
    },
    401: {
      backupMessage: "GEM was updated but failed to do so in MAX.",
      toastStyle: "warning",
      recoveryMessage: "You will need to disposition manually in MAX.",
      actionBtnTitle: "InContact"
    },
    404: {
      backupMessage: "GEM was updated but failed to do so in MAX.",
      toastStyle: "warning",
      recoveryMessage: "You will need to disposition manually in MAX.",
      actionBtnTitle: "InContact"
    },
    409: {
      backupMessage: "GEM was updated but failed to do so in MAX.",
      toastStyle: "warning",
      recoveryMessage: "You will need to disposition manually in MAX.",
      actionBtnTitle: "InContact"
    },
    default: {
      backupMessage: "Failed to disposition.",
      toastStyle: "error",
      recoveryMessage: "Please inform your supervisor."
    }
  },
  [Error.PostOpportunityNote]: {
    default: {
      backupMessage: "Failed to save the Opportunity note.",
      toastStyle: "error",
      recoveryMessage: ""
    }
  },
  [Error.PostSignalRNegotiate]: {
    default: {
      backupMessage: "Failed to connect to updater service.",
      toastStyle: "error",
      recoveryMessage: "Please contact your supervisor, and then log out and in again."
    }
  },
  [Error.PostToken]: {
    401: null,
    default: {
      backupMessage: "The server failed to validate your credentials.",
      toastStyle: "error",
      recoveryMessage: "Please check your credentials and try to log in again."
    }
  },
  [Error.PutPhoneNumbers]: {
    default: {
      backupMessage: "Failed to update the blocked phone number.",
      toastStyle: "error",
      recoveryMessage: "Please inform your supervisor."
    }
  },
  [Error.PutCenterMessage]: {
    default: {
      backupMessage: "There was an error updating the center message.",
      toastStyle: "error",
      recoveryMessage: "Please inform your supervisor."
    }
  },
  [Error.PutCenters]: {
    default: {
      backupMessage: "Unable to update the requested Centers.",
      toastStyle: "error",
      recoveryMessage: "Please submit a ticket for Center update."
    }
  },
  [Error.PutCustomer]: {
    400: {
      backupMessage: "Unable to update the Guest.",
      toastStyle: "warning",
      recoveryMessage: "Please check your entries for valid inputs.",
      actionBtnTitle: "info"
    },
    409: null,
    default: {
      backupMessage: "Unable to update the Guest.",
      toastStyle: "error",
      recoveryMessage: "Please inform your supervisor."
    }
  },
  [Error.PutCustomerReset]: {
    default: {
      backupMessage: "Unable to reset the old Guest.",
      toastStyle: "error",
      recoveryMessage: "Please inform your supervisor."
    }
  },
  [Error.PutCustomerNote]: {
    default: {
      backupMessage: "Unable to save the Guest Note.",
      toastStyle: "error",
      recoveryMessage: "Please update the Guest Note in Zenoti.",
      action: (props?: SessionProps | AppProps) => () => openNewTab(buildZenotiGuestPath(props as SessionProps)),
      actionBtnTitle: "Zenoti"
    }
  },
  [Error.PutIvrLeadState]: {
    default: {
      backupMessage: "Failed to switch MAX chat tab.",
      toastStyle: "warning",
      recoveryMessage: "Please inform your supervisor."
    }
  },
  [Error.PutIvrState]: {
    409: {
      backupMessage: "Failed to update MAX status.",
      toastStyle: "error",
      recoveryMessage: "Please open MAX then refresh the page.",
      action: (): (() => void) => reloadWindow,
      actionBtnTitle: "Refresh"
    },
    default: {
      backupMessage: "Unable to update your InContact status.",
      toastStyle: "error",
      recoveryMessage: "Please update your status manually.",
      actionBtnTitle: "InContact"
    }
  },
  [Error.PutIvrUserCampaigns]: {
    default: {
      backupMessage: "Failed to save user IVR skills.",
      toastStyle: "error",
      recoveryMessage: "Please submit a ticket for IVR skills."
    }
  },
  [Error.PutLead]: {
    default: {
      backupMessage: "Failed to create the Outbound Lead.",
      toastStyle: "error",
      recoveryMessage: "Please inform your supervisor."
    }
  },
  [Error.PutToken]: {
    default: {
      backupMessage: "Failed to authenticate with the server.",
      toastStyle: "error",
      recoveryMessage: "Please contact your supervisor, and then log out and in again."
    }
  },
  [Error.PutLeadResult]: {
    400: {
      backupMessage: "GEM was updated but failed to do so in MAX.",
      toastStyle: "warning",
      recoveryMessage: "You will need to disposition manually in MAX.",
      actionBtnTitle: "InContact"
    },
    401: {
      backupMessage: "GEM was updated but failed to do so in MAX.",
      toastStyle: "warning",
      recoveryMessage: "You will need to disposition manually in MAX.",
      actionBtnTitle: "InContact"
    },
    404: {
      backupMessage: "GEM was updated but failed to do so in MAX.",
      toastStyle: "warning",
      recoveryMessage: "You will need to disposition manually in MAX.",
      actionBtnTitle: "InContact"
    },
    default: {
      backupMessage: "Failed to Disposition.",
      toastStyle: "error",
      recoveryMessage: "Please inform your supervisor."
    }
  },
  [Error.PutUserPoll]: {
    default: {
      backupMessage: "Failed to respond to user poll.",
      toastStyle: "warning",
      recoveryMessage: "Please contact your supervisor and then refresh your browser."
    }
  },
  [Error.PutUserMe]: {
    default: {
      backupMessage: "Failed to update InContact credentials.",
      toastStyle: "error",
      recoveryMessage: "Please contact your supervisor, and then log out and in again."
    }
  },
  [Error.PutLeadReceived]: {
    default: {
      backupMessage: "Failed to indicate that Lead was received.",
      toastStyle: "warning",
      recoveryMessage: "Please inform your supervisor."
    }
  }
};

const dismissError = (error: Error): void => {
  if (toast.isActive(`requestErrorToast_${error}`)) toast.dismiss(`requestErrorToast_${error}`);
};

export { Error, ErrorBehaviors, dismissError };
